/**
 * TimelineLauncher — launch a timeline or any of its named sections, with a
 * LIVE mini-timeline visualisation of what the launched timeline is doing.
 *
 * The OPBridge timeline engine (`opbridge-timelines` crate) shipped + is live
 * on beta (S218); this component now dispatches real launches through it
 * (2026-08-16 — see OPApps/opbridge-opbuttons.md "RESPONSE 2026-08-16 (OPBridge
 * S218)" and `timeline/bridgeSync.ts`), on top of the SAME local visual sim
 * that already existed: a display-only playhead driven by the app's musical
 * clock (the same continuous `beat` the Header + SineLfo read). The local sim
 * stays even when connected — it's what makes the count-in (`trigger:
 * 'nextBar'`) and the mid-set-with-no-bridge rehearsal case both work, and it
 * visually tracks the real engine closely (same musical clock on both ends).
 *
 * 2026-08-21 — the real dispatch fires IMMEDIATELY on press, not gated on
 * the local count-in resolving. OPBridge's own `PlayheadRuntime::start()`
 * already quantizes a Song's `/start` to the next real downbeat
 * server-side; waiting for a second, independent local quantizer before
 * ever SENDING the command was redundant, and the two disagreeing is what
 * caused CP-103's 1-bar-late bug. The local count-in below is now purely
 * the operator-facing "waiting to launch" visual — real timing is entirely
 * OPBridge's job, matching the goal of the operator seeing OPButtons do the
 * work while OPBridge actually owns it under the hood.
 *
 * Sync model:
 *   - Driven by musical time (`props.beat()`), so it advances with the tempo /
 *     transport and holds when the transport is stopped. Practice clock drives
 *     it when disconnected.
 *   - `trigger: 'nextBar'` arms a count-in and starts exactly on the next bar
 *     boundary; `'instant'` starts immediately.
 *   - `loop` (resolved through the timeline's own loop toggle when 'inherit')
 *     wraps the playhead instead of stopping at the end.
 *   - Whole = play/stop toggle from bar 1. Section button = jump to that
 *     section (or, if it's the already-active section, stop).
 *
 * Real dispatch, per tier (OPBridge's actual HTTP surface — see
 * `bridgeSync.ts` module doc for the byte-verified detail):
 *   - `song`: whole = push + `/start`; section = push + `/jumpToSection` when
 *     the Song is already running, else push + `/start` + `/jumpToSection`;
 *     stop = `/stop`. `/start` is NOT idempotent: it replaces the running
 *     Song and drains its held notes (2026-09-30, next-update-plan 1.3).
 *   - `action`: whole/section both = push + `/fire` (fire-and-forget — there
 *     is no per-instance stop primitive server-side yet, so pressing "stop"
 *     on a running Action only clears the LOCAL visual, matching its
 *     fire-and-forget nature; the already-fired instance still plays out).
 *
 * Three modes (`item.mode`): `whole` / `whole+sections` / `sections`.
 */

import { For, Show, createSignal, createEffect, onCleanup, type Accessor } from 'solid-js';
import { editOwnsGesture, type TimelineLauncherItem, type LayoutColor } from '../layout/store.ts';
import { timelines } from '../timeline/store.ts';
import { timelineLoops, timelineTotalBars, timelineLeadIn, type Timeline } from '@opbuttons/schema';
import { playheadAt } from '../timeline/playhead.ts';
import { recordTimelineRuntime, clearTimelineRuntime } from '../timeline/runtime.ts';
import { sendMidiPanic, plainFireReason, type FireResult } from '../opbridge/dispatch.ts';
import { anyBridgeConnectedReactive, bridgeConnectedReactive, bridges, connectionsVersion, getConnection, primaryBridge, resolveBridge } from '../opbridge/store.ts';
import { commsIssue, spanText } from '../opbridge/comms.ts';
import {
  pushTimeline, fireTimelineAction, startTimelineAction, stopTimelineAction, jumpToSectionAction,
  summarizeTimelineDispatch, splitPushFailures, missedPartsNote, notLiveToastMessage, timelineHomeTarget, timelineDropWarning, timelinePartsDropWarning, launcherTargets, launcherDefinition,
  pressStillWanted,
} from '../timeline/bridgeSync.ts';
import { timelineStateFor, watchTimelineState, kickTimelineStatePoll } from '../timeline/statePoll.ts';
import { toastWithSong, withRepush, pushUnlessNewer } from '../timeline/launch.ts';
import { followTransport, transportRun, transportBar, noteTransportLaunch, noteTransportStop, kickTransport, stoppedHereRecently, claimExternalStop, timelineRunning } from '../timeline/transport.ts';
import { isTimelinePushCurrent, notePushOutcome, timelinePartsCurrent, pushTimelineParts, applyPartsOutcome, noteLaunchLoop } from '../timeline/autoSync.ts';
import { sectionColour } from '../timeline/sectionHue.ts';
import { flashBrightness } from '../layout/buttonLook.ts';
import { pushToast } from '../ui/toast.ts';
import { TrayTitle, trayAttrs } from './TrayTitle.tsx';

/** Push `t` to `target` only if OPBridge doesn't already have this exact
 *  content there (2026-08-23 latency pass - see autoSync.ts's module doc
 *  comment). Skipping is safe: `isTimelinePushCurrent` is keyed per
 *  RESOLVED target, so a fan-out Launch still pushes to any target that
 *  hasn't actually confirmed this content, even if another target has.
 *  Synthesizes a single `ok` result for a skipped target - same shape
 *  `pushTimeline` itself would have returned on a real successful push, so
 *  every downstream toast/summary path is unaffected by which branch ran. */
async function pushTimelineIfNeeded(t: Timeline, target: string, plan: LaunchPlan): Promise<FireResult[]> {
  // No "Sends to" of its own: the timeline's parts, each on its computer (2026-10-28, "the whole rig follows"), and the
  // press on one of them is carried to the rest. A full push here would overwrite that computer's part with the whole.
  if (plan.parts) {
    if (timelinePartsCurrent(t)) {
      const warning = timelinePartsDropWarning(t);
      return [warning ? { target, kind: 'ok', partialDropWarning: warning } : { target, kind: 'ok' }];
    }
    return pushUnlessNewer(t, target, async () => {
      // Live while it plays (2026-11-04, OPButtons 6): a stored-only copy left the running one behind.
      const { results } = await pushTimelineParts(t, { press: true, ...(timelineRunning(t.id) ? { live: true } : {}) });
      if (results.length > 0) applyPartsOutcome(t, results);
      return results.length > 0 ? results : [{ target, kind: 'ok' }];
    });
  }
  const followsHere = target === plan.follows;
  const siblings = plan.siblings.get(target) ?? new Set<string>();
  // The skip check compiles as the timeline's single home would, so it only
  // applies to the target carrying the steps that follow the launch; any
  // other target of a fan-out always pushes (cheap, and rare).
  if (followsHere && siblings.size === 0 && isTimelinePushCurrent(t, target)) {
    // A skipped push still names what won't play, so the toast never reads clean.
    const warning = timelineDropWarning(t, target);
    return [warning ? { target, kind: 'ok', partialDropWarning: warning } : { target, kind: 'ok' }];
  }
  return pushUnlessNewer(t, target, async () => {
    const results = await pushTimeline(t, target, { followsHere, siblings, press: true, ...(timelineRunning(t.id) ? { live: true } : {}) });
    if (followsHere && siblings.size === 0 && results.length > 0 && results.every((r) => r.kind === 'ok')) {
      notePushOutcome(t, results, target);
    }
    return results;
  });
}

/** Where one launch goes (2026-09-29). */
interface LaunchPlan {
  targets: string[];
  /** The one target that carries steps tied to no machine, so a plain note
   *  never sounds from two computers at once. */
  follows: string;
  /** Per target: the row ids of the launch's OTHER targets. */
  siblings: Map<string, Set<string>>;
  /** No "Sends to" of its own: the one target is the timeline's press target, and the push goes to its parts. */
  parts: boolean;
}

/** The launcher's own "Sends to" when the operator set one; otherwise the
 *  timeline's home - the machine its devices are on (bridgeSync.ts
 *  `timelinePlacement`). Before 2026-09-29 an unset launcher always meant
 *  "this device's primary", which put a Helix timeline on a computer with
 *  no Helix whenever the primary was not the Helix's machine. */
function planLaunch(t: Timeline, bridgeIds: string[] | undefined): LaunchPlan {
  const targets = launcherTargets(t, bridgeIds);
  const rowIds = targets.map((tg) => resolveBridge(tg || undefined)?.id ?? tg);
  const primaryId = primaryBridge()?.id;
  const primaryAt = primaryId ? rowIds.indexOf(primaryId) : -1;
  const follows = targets[primaryAt >= 0 ? primaryAt : 0] ?? '';
  const siblings = new Map<string, Set<string>>();
  targets.forEach((tg, i) => siblings.set(tg, new Set(rowIds.filter((_, j) => j !== i))));
  const parts = !(bridgeIds !== undefined && bridgeIds.some((id) => id !== ''));
  return { targets, follows, siblings, parts };
}

interface TimelineLauncherProps {
  item: TimelineLauncherItem;
  /** Continuous musical beat from the app's smoothClock (advances with tempo,
   *  freezes when the transport is stopped). Drives the live sim. */
  beat: Accessor<number>;
  /** Beats per bar (time-sig numerator, slaved or practice). */
  beatsPerBar: Accessor<number>;
  /** Tempo, for the beat pulse's 3-a-second cap (DEC-262). Absent = 120. */
  bpm?: Accessor<number>;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** Minimum divergence (in bars) between the local sim's playhead and
 *  OPBridge's own `songPlayheadBar` before a resync effect corrects it.
 *  Below this, normal poll latency/jitter (well under a bar at any
 *  reasonable tempo) is left alone so the playhead never visibly jumps on
 *  a routine tick — above it, something real diverged (an external pause/
 *  resume/jump) and the local view should snap to server truth. */
const DRIFT_RESYNC_BARS = 0.5;


export function TimelineLauncher(props: TimelineLauncherProps) {
  const tokenFor = () => COLOR_TOKEN[props.item.color];
  const timeline = () => timelines().find((t) => t.id === props.item.timelineId);
  const mode = () => props.item.mode ?? 'whole+sections';
  const trigger = (): 'instant' | 'nextBar' => props.item.trigger ?? 'nextBar';

  /** Resolve the launch's effective loop: `inherit` follows the timeline's own
   *  loop toggle; `on`/`off` override it. */
  function loops(t: Timeline): boolean {
    const l = props.item.loop ?? 'inherit';
    if (l === 'on') return true;
    if (l === 'off') return false;
    return timelineLoops(t);
  }

  /** The definition THIS launch plays: the timeline, carrying this
   *  launcher's Loop setting when it overrides (2026-09-30, next-update-plan
   *  1.7). Until then the Loop pill drove only the local sim - OPBridge was
   *  always pushed the timeline's OWN loop, so "Loop: On" over a non-looping
   *  timeline showed a looping playhead over a song that stopped at its end.
   *  OPBridge's `start` takes only an id and captures the pushed definition
   *  when the Song starts, so the loop travels in the definition, pushed
   *  right before the start. Only when it differs from what OPBridge holds:
   *  the push cache compares the compiled wire, `loops` included. */
  function launchDef(t: Timeline): Timeline {
    const def = launcherDefinition(t, props.item.loop);
    // Remembered for the run, so no automatic push undoes it mid-song (C-3).
    noteLaunchLoop(t.id, def === t ? null : timelineLoops(def));
    return def;
  }

  // ─── Live sim state ─────────────────────────────────────────────
  const [playing, setPlaying] = createSignal(false);
  const [armed, setArmed] = createSignal(false);      // count-in (trigger=nextBar)
  const [posBars, setPosBars] = createSignal(0);      // playhead position (fractional bars)
  // Plain refs (read inside the beat-driven effect; no reactivity needed).
  let launchBeat = 0;     // musical beat captured at launch
  let originBar = 0;      // timeline bar the playhead sat at when launchBeat was captured
  let armTarget = 0;      // beat of the bar boundary the count-in fires on
  let pendingOrigin = 0;  // originBar to adopt when the count-in fires

  // ─── Launch bookkeeping for the external-stop check (2026-09-30) ──
  // A Song counts as stopped from outside only once a state read REQUESTED
  // after OPBridge acknowledged this launch has seen it running, and a later
  // read sees it gone. Any earlier "nothing is playing" describes the server
  // BEFORE the launch. The old check compared a poll up to 5 s old and sent a
  // MIDI panic to every bridge on 8 of 8 launches - Instant ~100 ms after the
  // press, before /start had even left; Next bar on the song's first
  // downbeat - then showed "stopped" while the song played on, so the next
  // press restarted it (next-update-plan 1.1).
  let startAckedAt = Number.POSITIVE_INFINITY;  // performance.now() of OPBridge's ack; +Inf = none yet
  let seenRunning = false;                      // a read requested after that ack showed it running
  function resetLaunchTracking(): void {
    startAckedAt = Number.POSITIVE_INFINITY;
    seenRunning = false;
  }
  function noteStartAcked(): void {
    startAckedAt = performance.now();
    kickTimelineStatePoll();   // confirm within ~1 poll, not the 5 s idle cadence
  }
  const allOk = (rs: FireResult[] | undefined): boolean => !!rs && rs.length > 0 && rs.every((r) => r.kind === 'ok');

  /** Is this Song already running on OPBridge? Started by this launcher and
   *  acknowledged, or the followed bridge said so within the last 3 s (a
   *  start from another device). Decides whether a section press may just
   *  jump (next-update-plan 1.3). */
  function songRunningHere(t: Timeline): boolean {
    if ((playing() || armed()) && startAckedAt !== Number.POSITIVE_INFINITY) return true;
    const st = timelineStateFor(planLaunch(t, props.item.bridgeIds).follows);
    return !!st && st.snap.activeSongId === t.id && st.snap.songIsPlaying && performance.now() - st.requestedAt < 3_000;
  }

  /** True when at least one of this launcher's configured targets is
   *  connected - same reach as FireButton's own `anyTransportLive()`. Real
   *  dispatch is skipped (not attempted) when false; the local visual sim
   *  still runs regardless, same as it always has offline. */
  const live = () => {
    // Same targets a launch will use (planLaunch): the explicit "Sends to",
    // else the timeline's home - not this device's primary (2026-09-29).
    const ids = props.item.bridgeIds;
    if (ids !== undefined && ids.some((id) => id !== '')) return anyBridgeConnectedReactive(ids);
    const t = timeline();
    return t ? bridgeConnectedReactive(timelineHomeTarget(t)) : anyBridgeConnectedReactive(undefined);
  };

  /** A SONG timeline with its computer reachable follows OPBridge's own report (timeline/transport.ts, 2026-10-16):
   *  cued, playing, the playhead and Play / Stop are what the engine says for the bridge this launcher follows. The
   *  pad used to arm on the next bar of the clock it saw at the press, so a song that took the clock left it "cued"
   *  with no end, or started it 0.5-1.5 bars late (the transport audit). Action timelines and offline rehearsal keep
   *  the local sim below. */
  const follows = (): string => { const t = timeline(); return t ? planLaunch(t, props.item.bridgeIds).follows : ''; };
  const songFollow = (): boolean => timeline()?.tier === 'song' && live();
  createEffect(() => {
    if (!songFollow()) return;
    const release = followTransport(follows());
    onCleanup(release);
  });
  const bridgeRun = () => { const t = timeline(); const r = transportRun(follows()); return t && r && r.timelineId === t.id ? r : null; };
  /** The computer this song plays on, when it cannot be seen (2026-11-04, comms): the pad keeps its last place, stops
   *  moving and says so. Today it carried on as if playing; reading it as stopped would send the external-stop panic. */
  //
  // Judged on the computer the pad last SAW its run on, not on `follows()`: a song's home is worked out from which
  // computers are up, so the moment its computer goes, the home moves to another one that has no run, and the pad
  // would read the song as stopped. And not on `songFollow()`, which needs that computer live.
  const [seenOn, setSeenOn] = createSignal<string | null>(null);
  createEffect(() => { if (songFollow() && bridgeRun()) setSeenOn(resolveBridge(follows())?.id ?? null); });
  const unseen = (): { id: string; name: string } | null => {
    const t = timeline();
    const id = seenOn();
    if (!t || t.tier !== 'song' || !id) return null;
    const r = transportRun(id);
    if (!r || r.timelineId !== t.id || r.state === 'paused') return null;
    connectionsVersion();
    if (getConnection(id)?.statusSignal()() === 'connected') return null;
    return { id, name: bridges().find((b) => b.id === id)?.name?.trim() || 'its computer' };
  };
  const [heardTick, setHeardTick] = createSignal(0);
  const heardTimer = setInterval(() => { if (unseen()) setHeardTick((n) => n + 1); }, 1000);
  onCleanup(() => clearInterval(heardTimer));
  const lastHeard = (): string => { heardTick(); const u = unseen(); const since = u ? commsIssue(`lost:${u.id}`)?.since : undefined; return since ? `last heard ${spanText(Date.now() - since)} ago` : ''; };
  /** A run OPBridge reported playing, so its disappearing without a Stop here is an external stop. */
  let seenPlayingViaBridge = false;
  let stoppedHere = false;

  /** 2026-08-17 — a Launch that silently did nothing (every step's pedal
   *  binding failed to resolve, so nothing was ever pushed) was a real
   *  operator-hit bug on a live Song timeline ("TESTSONG"). A console.warn
   *  nobody was looking at is not a fix for that — every dispatch outcome
   *  now surfaces a toast, not just a log line. See
   *  `bridgeSync.ts::summarizeTimelineDispatch`. */
  function toastNotLive(t: Timeline): void {
    const s = notLiveToastMessage(t.name);
    pushToast(s.kind, s.message);
  }

  /** Defense in depth, not the primary fix (that's `pushTimelineOnBridge`'s
   *  own try/catch in bridgeSync.ts) — guarantees Launch/Stop can NEVER go
   *  silent even if something unanticipated throws in one of these chains. */
  function toastOnUnexpectedThrow(what: string, name: string, err: unknown): void {
    // eslint-disable-next-line no-console
    console.error(`[timeline] ${what} threw unexpectedly for "${name}":`, err);
    pushToast('error', `"${name}" failed to ${what}: ${err instanceof Error ? err.message : String(err)}`);
  }

  /** Push the timeline then fire it on OPBridge, on the targets
   *  {@link planLaunch} picks: the launcher's own "Sends to" when set, else
   *  the timeline's home. `action` = fire-and-forget; `song` = start from
   *  bar 0. One summary toast across all targets, not one per target. */
  function dispatchLaunch(t: Timeline): void {
    if (!live()) { toastNotLive(t); return; }
    const plan = planLaunch(t, props.item.bridgeIds);
    const targets = plan.targets;
    const def = launchDef(t);
    // A Stop pressed while the upload runs wins: what it was pressed after is dropped (2026-11-04, OPButtons 2).
    const wanted = targets.map((target) => pressStillWanted(target));
    void Promise.all(targets.map((target) => pushTimelineIfNeeded(def, target, plan))).then((perTarget) => {
      if (!wanted.every((w) => w())) return;
      // Only a press target's own part stops the start (audit C-2): another
      // computer that missed its part is named, and the rest plays.
      const { blocking, missed } = splitPushFailures(perTarget.flat(), targets);
      if (blocking.length > 0) {
        const s = summarizeTimelineDispatch(t.name, perTarget.flat(), null);
        pushToast(s.kind, s.message);
        return;
      }
      const pushResults = perTarget.flat().filter((r) => r.kind === 'ok');
      const noteMissed = withMissedNote(missedPartsNote(missed));
      // The launcher's Instant / Next bar now decides where OPBridge lands it
      // (S306 `quantize`), not only the count-in on screen (DEC-176, adapted).
      const fires = targets.map((target) => withRepush(
        t.id,
        () => (t.tier === 'song' ? startTimelineAction(t.id, target, trigger()) : fireTimelineAction(t.id, target, trigger())),
        () => pushTimelineIfNeeded(def, target, plan),
      ));
      void Promise.all(fires).then((fireResultsPerTarget) => {
        if (t.tier === 'song' && allOk(fireResultsPerTarget[targets.indexOf(plan.follows)])) noteStartAcked();
        const s = noteMissed(summarizeTimelineDispatch(t.name, pushResults, fireResultsPerTarget.flat()));
        toastWithSong(s, fireResultsPerTarget.flat());
      });
    }).catch((err: unknown) => toastOnUnexpectedThrow('launch', t.name, err));
  }

  /** Push the timeline, make sure it's the active Song, then jump to
   *  `sectionId`. When the Song is ALREADY running, only the jump is sent:
   *  OPBridge's `start` REPLACES the running Song and drains its held notes
   *  first (`opbridge-timelines` `start`), so the old start-then-jump
   *  restarted the song and cut every held note on each section press
   *  (next-update-plan 1.3, 2026-09-30). Action-tier has no per-section
   *  server primitive yet, so it just fires the whole timeline from the top
   *  (documented in the file header). One summary toast across all targets. */
  function dispatchLaunchSection(t: Timeline, sectionId: string, alreadyRunning: boolean): void {
    if (!live()) { toastNotLive(t); return; }
    const plan = planLaunch(t, props.item.bridgeIds);
    const targets = plan.targets;
    const def = launchDef(t);
    // A Stop pressed while the upload runs wins: what it was pressed after is dropped (2026-11-04, OPButtons 2).
    const wanted = targets.map((target) => pressStillWanted(target));
    void Promise.all(targets.map((target) => pushTimelineIfNeeded(def, target, plan))).then((perTarget) => {
      if (!wanted.every((w) => w())) return;
      // Only a press target's own part stops the start (audit C-2): another
      // computer that missed its part is named, and the rest plays.
      const { blocking, missed } = splitPushFailures(perTarget.flat(), targets);
      if (blocking.length > 0) {
        const s = summarizeTimelineDispatch(t.name, perTarget.flat(), null);
        pushToast(s.kind, s.message);
        return;
      }
      const pushResults = perTarget.flat().filter((r) => r.kind === 'ok');
      const noteMissed = withMissedNote(missedPartsNote(missed));
      if (t.tier === 'song') {
        void Promise.all(targets.map((target) =>
          alreadyRunning
            ? jumpToSectionAction(sectionId, target)
            : withRepush(t.id, () => startTimelineAction(t.id, target, trigger()), () => pushTimelineIfNeeded(def, target, plan)).then((started) => {
                if (target === plan.follows && allOk(started)) noteStartAcked();
                return jumpToSectionAction(sectionId, target);
              }),
        )).then((jumpResultsPerTarget) => {
          // A jump OPBridge accepted proves the Song is active there - the
          // anchor for the external-stop check when another device started it.
          if (alreadyRunning && startAckedAt === Number.POSITIVE_INFINITY
            && allOk(jumpResultsPerTarget[targets.indexOf(plan.follows)])) noteStartAcked();
          const s = noteMissed(summarizeTimelineDispatch(t.name, pushResults, jumpResultsPerTarget.flat()));
          pushToast(s.kind, s.message);
        });
      } else {
        void Promise.all(targets.map((target) => withRepush(t.id, () => fireTimelineAction(t.id, target, trigger()), () => pushTimelineIfNeeded(def, target, plan)))).then((fireResultsPerTarget) => {
          const s = noteMissed(summarizeTimelineDispatch(t.name, pushResults, fireResultsPerTarget.flat()));
          pushToast(s.kind, s.message);
        });
      }
    }).catch((err: unknown) => toastOnUnexpectedThrow('launch', t.name, err));
  }

  /** Appends the missed-parts note to a non-error summary, as a warning. */
  function withMissedNote(note: string | null) {
    return <S extends { kind: string; message: string }>(s: S): S | { kind: 'warn'; message: string } =>
      note && s.kind !== 'error' ? { kind: 'warn' as const, message: `${s.message} ${note}` } : s;
  }

  function dispatchStop(t: Timeline): void {
    if (!live() || t.tier !== 'song') return; // Action has no server-side stop (fire-and-forget)
    const targets = planLaunch(t, props.item.bridgeIds).targets;
    void Promise.all(targets.map((target) => stopTimelineAction(target))).then((perTarget) => {
      const failed = perTarget.flat().filter((r) => r.kind !== 'ok');
      if (failed.length > 0) {
        pushToast('error', `"${t.name}" did not stop: ${plainFireReason(failed[0]!)}.`);
      }
    }).catch((err: unknown) => toastOnUnexpectedThrow('stop', t.name, err));
  }

  function total(t: Timeline): number {
    return Math.max(1, timelineTotalBars(t));
  }
  function sectionStartBar(t: Timeline, sectionId: string): number {
    let acc = timelineLeadIn(t);   // sections start after the lead-in (`firstSectionBar`)
    for (const s of t.sections) {
      if (s.id === sectionId) return acc;
      acc += Math.max(0, s.lengthBars | 0);
    }
    return 0;
  }

  function anchorNow(startBar: number): void {
    originBar = startBar;
    launchBeat = props.beat();
    setArmed(false);
    setPlaying(true);
    setPosBars(startBar);
  }
  function armFor(startBar: number): void {
    const bpBar = Math.max(1, props.beatsPerBar());
    armTarget = (Math.floor(props.beat() / bpBar) + 1) * bpBar;   // next whole bar
    pendingOrigin = startBar;
    setPlaying(false);
    setArmed(true);
    setPosBars(startBar);
  }
  function stop(): void {
    setPlaying(false);
    setArmed(false);
    resetLaunchTracking();
    const t = timeline();
    if (t && songFollow()) { stoppedHere = true; seenPlayingViaBridge = false; noteTransportStop(follows()); }
    if (t) dispatchStop(t);
  }

  /** Which section the playhead is inside right now (null when idle). */
  const activeSectionId = (): string | null => {
    const t = timeline();
    if (!t || (!playing() && !armed())) return null;
    const p = posBars();
    let acc = timelineLeadIn(t);
    if (p < acc) return null;   // in the lead-in: no section is playing yet
    for (const s of t.sections) {
      const len = Math.max(0, s.lengthBars | 0);
      if (p < acc + len) return s.id;
      acc += len;
    }
    return t.sections[t.sections.length - 1]?.id ?? null;
  };

  const playheadPct = (): number => {
    const t = timeline();
    if (!t) return 0;
    return Math.max(0, Math.min(100, (posBars() / total(t)) * 100));
  };
  const barReadout = (): string => {
    const bpBar = Math.max(1, props.beatsPerBar());
    const p = posBars();
    const bar = Math.floor(p) + 1;
    const beat = Math.floor((p - Math.floor(p)) * bpBar) + 1;
    return `${bar}.${beat}`;
  };

  // Beat-driven advance. Re-runs on every smoothClock tick (like SineLfo's
  // phase). Handles the count-in → launch transition, wrapping / end-stop, and
  // updates the playhead position. Converges (terminal states early-return), so
  // setting playing/armed here can't loop.
  createEffect(() => {
    const b = props.beat();
    const t = timeline();
    if (!t) return;
    const bpBar = Math.max(1, props.beatsPerBar());
    const tot = total(t);
    // Its computer cannot be seen: everything holds where it was (2026-11-04, comms). Following stops with the
    // connection, and the local simulation below would start from anchors the follow path never set.
    if (unseen()) return;
    if (songFollow()) {
      const r = bridgeRun();
      if (r) {
        stoppedHere = false;
        const at = transportBar(follows(), t);
        // A song that does not loop PAUSES at its end on OPBridge: finished, not stopped.
        if (r.state === 'paused' && !loops(t) && r.parkedBar >= tot - 0.05) { setArmed(false); setPlaying(false); setPosBars(tot); seenPlayingViaBridge = false; return; }
        if (r.state === 'playing') seenPlayingViaBridge = true;
        setArmed(r.state === 'queued');
        setPlaying(r.state !== 'queued');
        // Frozen while its computer cannot be seen: the glide would be a guess (2026-11-04, comms).
        if (at && !unseen()) setPosBars(Math.max(0, at.pos));
        return;
      }
      // Another song now runs there: this one was REPLACED, not stopped (2026-11-04 timeline audit, OPButtons 1: song A's
      // pad sent the 48-message panic at every switch to song B, 67 ms after the press, while A still played to the
      // line; `_probe_song_switch_panic`). OPBridge ends a replaced song's notes itself.
      const replaced = !!transportRun(follows());
      if (seenPlayingViaBridge && !replaced && !stoppedHere && !stoppedHereRecently(follows(), t.id) && claimExternalStop(follows(), t.id)) {
        // Operator-reported 2026-08-23: stopping the transport in Ableton directly can leave notes the song had turned
        // on sounding; an external stop we did not initiate gets an automatic MIDI panic (kept from the state-poll
        // reconcile this replaces for songs).
        void sendMidiPanic();
        pushToast('warn', 'Ableton stopped externally - sent MIDI panic to clear any stuck notes');
      }
      seenPlayingViaBridge = false;
      if (playing() || armed()) { setPlaying(false); setArmed(false); }
      return;
    }
    if (armed()) {
      if (b >= armTarget) {
        originBar = pendingOrigin;
        launchBeat = armTarget;   // anchor exactly on the bar boundary
        setArmed(false);
        setPlaying(true);
        setPosBars(originBar);
        // Real dispatch already fired immediately when the arm was set —
        // see launchWhole/launchSection. This is purely the visual
        // resolution now.
      }
      return;
    }
    if (!playing()) return;
    // Walked forward from the origin through each section's end rule (DEC-227: a looping section
    // replays until a section button moves on; OPBridge plays the same behind timeline_section_loop).
    const at = playheadAt(t, originBar, (b - launchBeat) / bpBar, loops(t));
    if (at.ended) {
      setPlaying(false);
      setPosBars(tot);
      return;
    }
    setPosBars(at.pos);
  });

  // Reconcile against OPBridge's own Song state (2026-08-21) — closes the
  // gap where a Song paused/resumed/stopped by anything OTHER than this
  // launcher (the OPBridge dashboard, a second device) never reached the
  // local visual sim, which just kept ticking forward on its own musical-
  // clock math regardless of what actually happened server-side. See
  // timeline/statePoll.ts.
  //
  // Deliberately narrow: only touches a Song-tier timeline that's already
  // PLAYING locally (`playing()`) — never `armed()`, since a local count-in
  // for OUR OWN pending launch has no server truth to compare against yet
  // (the real /start hasn't been sent). Does nothing while offline — the
  // local sim is deliberately authoritative in that case (see file header,
  // "mid-set-with-no-bridge rehearsal"). A big drift gets a hard resync,
  // matching DEC-092's slew-correct precedent (small drift is left alone so
  // the playhead doesn't visibly jump every poll tick); a stopped-elsewhere
  // Song just clears the local display without re-sending a stop the server
  // already knows about.
  //
  // 2026-09-30: reads the bridge this launcher FOLLOWS (a timeline plays on
  // its home, DEC-224, not necessarily the primary), and judges only reads
  // requested after OPBridge acknowledged the launch - see "Launch
  // bookkeeping" above for the false panic that fixed.
  createEffect(() => {
    const t = timeline();
    if (!t || t.tier !== 'song') return;
    const unwatch = watchTimelineState(planLaunch(t, props.item.bridgeIds).follows);
    onCleanup(unwatch);
  });
  createEffect(() => {
    const t = timeline();
    if (!t || t.tier !== 'song' || !playing() || !live() || songFollow()) return;
    const stamped = timelineStateFor(planLaunch(t, props.item.bridgeIds).follows);
    // Requested before OPBridge acknowledged this launch: it describes the
    // server before the launch, so it can neither confirm nor contradict it.
    if (!stamped || stamped.requestedAt <= startAckedAt) return;
    const snap = stamped.snap;
    const running = snap.activeSongId === t.id && snap.songIsPlaying;
    if (running) seenRunning = true;
    if (!running) {
      // Never seen running since the launch: the start failed (its own toast
      // says so) or has not landed yet. Not an external stop.
      if (!seenRunning) return;
      // A Song that does not loop PAUSES at its end on OPBridge (same id, not
      // playing, playhead pinned at the total), and OPBridge flushes its notes
      // on that path. Finished, not stopped: no panic.
      if (snap.activeSongId === t.id && !loops(t) && (snap.songPlayheadBar ?? 0) >= total(t) - 0.05) {
        resetLaunchTracking();
        setPlaying(false);
        setPosBars(total(t));
        return;
      }
      resetLaunchTracking();
      setPlaying(false);
      setArmed(false);
      // Operator-reported 2026-08-23: stopping the transport in Ableton
      // directly (not via this launcher's own Stop) can leave notes the
      // Song had already turned on stuck sounding forever - a jump back in
      // the server's musical clock reanchors OPBridge's timeline runtime
      // without ever firing the now-skipped note-off steps that were still
      // pending ahead of it (suspected root cause written up for OPBridge in
      // OPApps/opbridge-opbuttons.md - opbridge-timelines/src/lib.rs's
      // `advance()`, the `beat_jumped_back` reanchor branch has no
      // fire-due-steps flush before it, unlike the end-of-timeline wrap path
      // right above it which explicitly added one for the same reason,
      // S228). This is the stopgap on OUR side regardless of that fix
      // landing: an external stop we didn't initiate gets an automatic MIDI
      // panic (notes/sound off, all channels) so a stuck note can't outlive
      // the session waiting on an operator to notice and hit Panic by hand.
      void sendMidiPanic();
      pushToast('warn', 'Ableton stopped externally - sent MIDI panic to clear any stuck notes');
      return;
    }
    if (snap.songPlayheadBar === null) return;
    const tot = total(t);
    let drift = Math.abs(posBars() - snap.songPlayheadBar);
    if (loops(t)) drift = Math.min(drift, tot - drift); // circular distance across the loop seam
    if (drift > DRIFT_RESYNC_BARS) {
      originBar = snap.songPlayheadBar;
      launchBeat = props.beat();
      setPosBars(snap.songPlayheadBar);
    }
  });

  // Mirror the sim out to the shared timeline-runtime store so a DisplayItem
  // bound to this launcher can read it (source: 'timeline'). Pure additive
  // write-out - reads the already-computed values above, writes to an
  // external signal, changes nothing about this component's own rendering.
  createEffect(() => {
    recordTimelineRuntime(props.item.id, {
      playing: playing(),
      armed: armed(),
      posBars: posBars(),
      activeSectionId: activeSectionId(),
      playheadPct: playheadPct(),
      barReadout: barReadout(),
    });
  });
  onCleanup(() => clearTimelineRuntime(props.item.id));

  // ─── Launch handlers ────────────────────────────────────────────
  function launchWhole(): void {
    if (editOwnsGesture(props.item.id)) return;
    const t = timeline();
    if (!t) return;
    // Toggle off what is REALLY running (2026-10-16): a song started from the editor, the iPad or a pedal shows here,
    // so this stops it rather than starting it again from the top.
    if (playing() || armed() || (songFollow() && bridgeRun())) { stop(); return; }
    // Real dispatch fires immediately regardless of trigger mode — see the
    // 2026-08-21 file-header note. The trigger only shapes the LOCAL visual.
    resetLaunchTracking();
    dispatchLaunch(t);
    if (songFollow()) { stoppedHere = false; noteTransportLaunch(follows(), t.id, 0); return; }
    if (trigger() === 'instant') {
      anchorNow(0);
    } else {
      armFor(0);
    }
  }

  function launchSection(sectionId: string): void {
    if (editOwnsGesture(props.item.id)) return;
    const t = timeline();
    if (!t) return;
    const s = t.sections.find((sec) => sec.id === sectionId);
    if (!s) return;
    // Pressing the already-active section stops; otherwise jump to it.
    if ((playing() || armed()) && activeSectionId() === sectionId) { stop(); return; }
    const startBar = sectionStartBar(t, sectionId);
    const alreadyRunning = t.tier === 'song' && (songFollow() ? !!bridgeRun() : songRunningHere(t));
    if (!alreadyRunning) resetLaunchTracking();
    dispatchLaunchSection(t, sectionId, alreadyRunning);
    if (songFollow()) {
      // The jump or start lands on OPBridge's bar line; the transport shows it as it happens.
      if (!alreadyRunning) { stoppedHere = false; noteTransportLaunch(follows(), t.id, startBar); } else { setJumpPress({ id: sectionId, at: performance.now() }); kickTransport(); }
      return;
    }
    if (trigger() === 'instant') {
      anchorNow(startBar);
    } else {
      armFor(startBar);
    }
  }

  // ---- DEC-321: the section that plays floods in its colour and pulses on the beat; a press that waits for the bar
  // line shows the amber ring and the beats left. Visual only: the launch was dispatched when it was pressed. ----
  let rootEl: HTMLDivElement | undefined;
  const [waitCount, setWaitCount] = createSignal(0);
  // A section pressed while the song PLAYS waits for its bar line too, and says so (DEC-321; 2026-11-04 timeline audit,
  // OPButtons 5: a jump showed nothing for up to a bar). The section OPBridge names (`pendingJumpBar`) once a reading
  // carries it, the pressed one until then; gone when it lands, or after a bar and a second if it never does.
  const [jumpPress, setJumpPress] = createSignal<{ id: string; at: number } | null>(null);
  const waitingJump = (): string | null => {
    const t = timeline();
    if (!t || !playing() || armed()) return null;
    const pj = songFollow() ? bridgeRun()?.pendingJumpBar : null;
    if (typeof pj === 'number') {
      const s = t.sections.find((x) => Math.abs(sectionStartBar(t, x.id) - pj) < 1e-6);
      if (s) return s.id;
    }
    const p = jumpPress();
    const barMs = (60_000 / Math.max(1, props.bpm?.() ?? 120)) * Math.max(1, props.beatsPerBar());
    props.beat();   // re-checked as time passes
    return p && activeSectionId() !== p.id && performance.now() - p.at < barMs + 1000 ? p.id : null;
  };
  createEffect(() => {
    const a = armed(), p = playing();
    if (!a && !p) { rootEl?.style.removeProperty('--bp'); rootEl?.style.removeProperty('--qp'); return; }
    const b = props.beat();
    const bpBar = Math.max(1, props.beatsPerBar());
    if (!a && waitingJump() !== null) {
      // To the next bar line, where OPBridge lands the jump; the playing section keeps its pulse.
      const left = bpBar - (((b % bpBar) + bpBar) % bpBar);
      rootEl?.style.setProperty('--qp', (1 - left / bpBar).toFixed(3));
      setWaitCount(Math.max(1, Math.ceil(left - 1e-9)));
      rootEl?.style.setProperty('--bp', flashBrightness('pulse', b, props.bpm?.() ?? 120).toFixed(3));
      return;
    }
    if (a) {
      // The local count-in knows its line (armTarget); a song OPBridge holds queued shows the full ring.
      const left = songFollow() ? bpBar : Math.max(0, armTarget - b);
      rootEl?.style.setProperty('--qp', (1 - left / bpBar).toFixed(3));
      setWaitCount(songFollow() ? 0 : Math.max(1, Math.ceil(left - 1e-9)));
      rootEl?.style.removeProperty('--bp');
    } else {
      rootEl?.style.removeProperty('--qp');
      rootEl?.style.setProperty('--bp', flashBrightness('pulse', b, props.bpm?.() ?? 120).toFixed(3));
    }
  });
  const waitRing = () => (
    <>
      <span class="bg-cell-q" aria-hidden="true" />
      <Show when={waitCount() > 0}><span class="bg-cell-qn" aria-hidden="true">{waitCount()}</span></Show>
    </>
  );

  return (
    <div
      ref={(el) => (rootEl = el)}
      {...trayAttrs(props.item)}
      class="timeline-launcher"
      data-token={tokenFor()}
      data-mode={mode()}
      data-playing={playing() ? 'true' : 'false'}
      data-armed={armed() ? 'true' : 'false'}
      data-unseen={unseen() ? 'true' : undefined}
      style={unseen() ? { position: 'relative' } : undefined}
    >
      <TrayTitle item={props.item} />
      <Show when={unseen()}>
        {(u) => <div class="comms-lost-band" aria-live="polite"><i />Can't see {u().name}<span>{lastHeard()}</span></div>}
      </Show>
      <Show
        when={timeline()}
        fallback={
          <div class="timeline-launcher-empty">
            <div class="timeline-launcher-empty-title">TIMELINE LAUNCHER</div>
            <div class="timeline-launcher-empty-sub">
              Pick a timeline in the Inspector.
            </div>
          </div>
        }
      >
        {(t) => (
          <>
            <div class="timeline-launcher-header">
              <span class="timeline-launcher-tier" data-tier={t().tier}>{t().tier}</span>
              <span class="timeline-launcher-name">{t().name}</span>
              <Show when={loops(t())}>
                <span class="timeline-launcher-loop" title="Loops on launch" aria-label="loops">⟳</span>
              </Show>
              <Show when={playing() || armed()}>
                <span class="timeline-launcher-live" data-armed={armed() ? 'true' : 'false'} aria-hidden="true" />
              </Show>
            </div>

            {/* Live mini-timeline — sections as proportional bands + a sweeping
                playhead. Appears while launched/armed. */}
            <Show when={playing() || armed()}>
              <div class="timeline-launcher-viz" data-armed={armed() ? 'true' : 'false'}>
                <div class="timeline-launcher-viz-strip">
                  <For each={t().sections}>
                    {(section) => (
                      <div
                        class="tl-viz-sec"
                        data-active={activeSectionId() === section.id ? 'true' : 'false'}
                        style={{
                          'flex-grow': String(Math.max(1, section.lengthBars | 0)),
                          // The one section colour scheme (timeline/sectionHue.ts, DEC-281): a CHORUS is the same pink
                          // here as on the song row, the Now playing Display and Navigate. Was a hash of the name.
                          '--sec-c': sectionColour(section),
                        }}
                        title={section.name}
                      />
                    )}
                  </For>
                  <Show when={!armed()}>
                    <div class="tl-viz-head" style={{ left: `${playheadPct()}%` }} aria-hidden="true" />
                  </Show>
                </div>
                <div class="tl-viz-readout">{armed() ? 'cued' : `bar ${barReadout()}`}</div>
              </div>
            </Show>

            <div class="timeline-launcher-body">
              <Show when={mode() === 'whole' || mode() === 'whole+sections'}>
                <button
                  type="button"
                  class="timeline-launcher-whole"
                  data-active={playing() ? 'true' : 'false'}
                  data-waiting={armed() && activeSectionId() === null ? 'true' : undefined}
                  onPointerDown={(e) => { e.stopPropagation(); }}
                  onClick={(e) => { e.stopPropagation(); launchWhole(); }}
                >
                  <span class="timeline-launcher-play">{playing() || armed() ? '❚❚' : '▶'}</span>
                  <span class="timeline-launcher-whole-label">Whole</span>
                  <Show when={armed() && activeSectionId() === null}>{waitRing()}</Show>
                </button>
              </Show>

              <Show when={mode() === 'whole+sections' || mode() === 'sections'}>
                <div class="timeline-launcher-sections">
                  <For each={t().sections}>
                    {(section) => (
                      <button
                        type="button"
                        class="timeline-launcher-section"
                        data-sec=""
                        style={{ '--sc': sectionColour(section) }}
                        data-active={playing() && activeSectionId() === section.id ? 'true' : 'false'}
                        data-waiting={(armed() && activeSectionId() === section.id) || waitingJump() === section.id ? 'true' : undefined}
                        onPointerDown={(e) => { e.stopPropagation(); }}
                        onClick={(e) => { e.stopPropagation(); launchSection(section.id); }}
                      >
                        {section.name}
                        <Show when={(armed() && activeSectionId() === section.id) || waitingJump() === section.id}>{waitRing()}</Show>
                      </button>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </>
        )}
      </Show>
    </div>
  );
}
